import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatCurrency, formatDate } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, UserCog, Banknote, Clock, History, Download, Upload, CheckCircle } from 'lucide-react';
import ExportButton from '../components/ExportButton';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const positions = [
  { en: 'Manager', ar: 'مدير' },
  { en: 'Accountant', ar: 'محاسب' },
  { en: 'Sales', ar: 'مبيعات' },
  { en: 'Baker', ar: 'خباز' },
  { en: 'Cashier', ar: 'كاشير' },
  { en: 'Delivery', ar: 'توصيل' },
  { en: 'Chef', ar: 'شيف' },
  { en: 'Other', ar: 'أخرى' },
];

const salaryTypes = [
  { en: 'daily', ar: 'يومي' },
  { en: 'weekly', ar: 'أسبوعي' },
  { en: 'monthly', ar: 'شهري' },
];

const paymentTypes = [
  { en: 'salary', ar: 'راتب' },
  { en: 'advance', ar: 'سلفة' },
  { en: 'bonus', ar: 'مكافأة' },
  { en: 'deduction', ar: 'خصم' },
];

export default function EmployeesPage() {
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('employees');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    position: '',
    salary: '',
    salary_type: 'monthly',
    hire_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [salaryData, setSalaryData] = useState({
    payment_type: 'salary',
    period_type: 'monthly',
    period_date: new Date().toISOString().split('T')[0],
    amount: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const [employeesRes, paymentsRes] = await Promise.allSettled([
        axios.get(`${API}/employees`),
        axios.get(`${API}/salary-payments`),
      ]);
      if (employeesRes.status === 'fulfilled') {
        setEmployees(employeesRes.value.data);
      }
      if (paymentsRes.status === 'fulfilled') {
        setSalaryPayments(paymentsRes.value.data);
      }
      if (employeesRes.status === 'rejected' && paymentsRes.status === 'rejected') {
        toast.error(language === 'ar' ? 'خطأ في تحميل البيانات' : 'Error loading data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(language === 'ar' ? 'خطأ في تحميل البيانات' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-export to Excel after any change
  const autoExportToExcel = (employeesList, paymentsList) => {
    try {
      // Employees sheet
      const employeesData = employeesList.map((emp, index) => ({
        '#': index + 1,
        'Name': emp.name,
        'Position': emp.position,
        'Phone': emp.phone || '',
        'Email': emp.email || '',
        'Salary Type': emp.salary_type || 'monthly',
        'Salary (£)': emp.salary,
        'Hire Date': emp.hire_date ? formatDate(emp.hire_date) : '',
        'Notes': emp.notes || '',
        'ID': emp.id,
      }));

      // Payments sheet
      const paymentsData = paymentsList.map((pay, index) => ({
        '#': index + 1,
        'Date': formatDate(pay.date),
        'Employee': pay.employee_name,
        'Type': pay.payment_type,
        'Period': pay.period_type,
        'Period Date': pay.period_date ? formatDate(pay.period_date) : '',
        'Amount (£)': pay.amount,
        'Notes': pay.notes || '',
      }));

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();
      
      // Style the sheets
      const wsEmployees = XLSX.utils.json_to_sheet(employeesData);
      wsEmployees['!cols'] = [
        { wch: 5 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 25 },
        { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 30 }, { wch: 40 }
      ];
      XLSX.utils.book_append_sheet(wb, wsEmployees, 'Employees');
      
      const wsPayments = XLSX.utils.json_to_sheet(paymentsData);
      wsPayments['!cols'] = [
        { wch: 5 }, { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 12 },
        { wch: 15 }, { wch: 12 }, { wch: 30 }
      ];
      XLSX.utils.book_append_sheet(wb, wsPayments, 'Salary Payments');

      // Save to localStorage as backup
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'base64' });
      localStorage.setItem('qassion_employees_excel', wbout);
      
      return wb;
    } catch (error) {
      console.error('Error creating Excel:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        salary: parseFloat(formData.salary),
      };

      let response;
      if (editingEmployee) {
        response = await axios.put(`${API}/employees/${editingEmployee.id}`, data);
        toast.success(
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div>
              <p className="font-medium">{language === 'ar' ? 'تم التحديث بنجاح!' : 'Updated Successfully!'}</p>
              <p className="text-sm text-muted-foreground">
                {language === 'ar' ? 'تم تحديث بيانات الموظف في قاعدة البيانات وملف Excel' : 'Employee updated in database and Excel file'}
              </p>
            </div>
          </div>
        );
      } else {
        response = await axios.post(`${API}/employees`, data);
        toast.success(
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div>
              <p className="font-medium">{language === 'ar' ? 'تمت الإضافة بنجاح!' : 'Added Successfully!'}</p>
              <p className="text-sm text-muted-foreground">
                {language === 'ar' ? 'تم إضافة الموظف إلى قاعدة البيانات ولوحة التحكم وملف Excel' : 'Employee added to database, dashboard and Excel file'}
              </p>
            </div>
          </div>
        );
      }
      
      // Fetch updated data
      await fetchData();
      
      // Auto-update Excel after successful save
      const updatedEmployees = editingEmployee 
        ? employees.map(emp => emp.id === editingEmployee.id ? { ...emp, ...data } : emp)
        : [...employees, response.data];
      autoExportToExcel(updatedEmployees, salaryPayments);
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving employee:', error);
      toast.error(language === 'ar' ? 'خطأ في حفظ البيانات' : 'Error saving data');
    }
  };

  const handlePaySalary = async (e) => {
    e.preventDefault();
    try {
      const paymentData = {
        employee_id: selectedEmployee.id,
        employee_name: selectedEmployee.name,
        amount: parseFloat(salaryData.amount),
        payment_type: salaryData.payment_type,
        period_type: salaryData.period_type,
        period_date: salaryData.period_date,
        notes: salaryData.notes,
      };
      
      await axios.post(`${API}/salary-payments`, paymentData);
      
      toast.success(
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <div>
            <p className="font-medium">{language === 'ar' ? 'تم دفع الراتب!' : 'Salary Paid!'}</p>
            <p className="text-sm text-muted-foreground">
              {formatCurrency(parseFloat(salaryData.amount))} - {selectedEmployee.name}
            </p>
          </div>
        </div>
      );
      
      await fetchData();
      
      // Auto-update Excel
      const newPayment = { ...paymentData, date: new Date().toISOString() };
      autoExportToExcel(employees, [...salaryPayments, newPayment]);
      
      setSalaryDialogOpen(false);
      setSelectedEmployee(null);
      setSalaryData({
        payment_type: 'salary',
        period_type: 'monthly',
        period_date: new Date().toISOString().split('T')[0],
        amount: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error paying salary:', error);
      toast.error(language === 'ar' ? 'خطأ في دفع الراتب' : 'Error paying salary');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا الموظف؟' : 'Are you sure you want to delete this employee?')) {
      try {
        await axios.delete(`${API}/employees/${id}`);
        
        toast.success(language === 'ar' ? 'تم حذف الموظف بنجاح' : 'Employee deleted successfully');
        
        const updatedEmployees = employees.filter(emp => emp.id !== id);
        setEmployees(updatedEmployees);
        autoExportToExcel(updatedEmployees, salaryPayments);
      } catch (error) {
        console.error('Error deleting employee:', error);
        toast.error(language === 'ar' ? 'خطأ في حذف الموظف' : 'Error deleting employee');
      }
    }
  };

  const handleDeletePayment = async (id) => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) {
      try {
        await axios.delete(`${API}/salary-payments/${id}`);
        
        const updatedPayments = salaryPayments.filter(p => p.id !== id);
        setSalaryPayments(updatedPayments);
        autoExportToExcel(employees, updatedPayments);
        
        toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
      } catch (error) {
        console.error('Error deleting payment:', error);
        toast.error(language === 'ar' ? 'خطأ في الحذف' : 'Error deleting');
      }
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      phone: employee.phone || '',
      email: employee.email || '',
      position: employee.position,
      salary: employee.salary.toString(),
      salary_type: employee.salary_type || 'monthly',
      hire_date: employee.hire_date?.split('T')[0] || '',
      notes: employee.notes || '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmployee(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      position: '',
      salary: '',
      salary_type: 'monthly',
      hire_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
  };

  const openSalaryDialog = (employee) => {
    setSelectedEmployee(employee);
    setSalaryData({
      payment_type: 'salary',
      period_type: employee.salary_type || 'monthly',
      period_date: new Date().toISOString().split('T')[0],
      amount: employee.salary.toString(),
      notes: '',
    });
    setSalaryDialogOpen(true);
  };

  // Export to Excel file download
  const handleExportExcel = () => {
    const wb = autoExportToExcel(employees, salaryPayments);
    if (wb) {
      const date = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `qassion_employees_${date}.xlsx`);
      toast.success(language === 'ar' ? 'تم تصدير الملف بنجاح!' : 'File exported successfully!');
    }
  };

  // Import from Excel
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        
        // Import employees
        const wsEmployees = wb.Sheets['Employees'];
        if (wsEmployees) {
          const data = XLSX.utils.sheet_to_json(wsEmployees);
          
          let addedCount = 0;
          for (const row of data) {
            // Skip if employee already exists (by name)
            const exists = employees.some(emp => emp.name === row['Name']);
            if (exists) continue;
            
            try {
              await axios.post(`${API}/employees`, {
                name: row['Name'] || '',
                position: row['Position'] || 'Other',
                phone: row['Phone'] || '',
                email: row['Email'] || '',
                salary_type: row['Salary Type'] || 'monthly',
                salary: parseFloat(row['Salary (£)']) || 0,
                hire_date: row['Hire Date'] || new Date().toISOString().split('T')[0],
                notes: row['Notes'] || '',
              });
              addedCount++;
            } catch (err) {
              console.error('Error importing employee:', row['Name'], err);
            }
          }
          
          await fetchData();
          toast.success(
            <div>
              <p className="font-medium">{language === 'ar' ? 'تم الاستيراد بنجاح!' : 'Import Successful!'}</p>
              <p className="text-sm">{addedCount} {language === 'ar' ? 'موظف جديد' : 'new employees added'}</p>
            </div>
          );
        }
      } catch (error) {
        console.error('Import error:', error);
        toast.error(language === 'ar' ? 'خطأ في الاستيراد' : 'Import error');
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const filteredEmployees = useMemo(() => 
    employees.filter(
      (e) =>
        e.name?.toLowerCase().includes(search.toLowerCase()) ||
        e.position?.toLowerCase().includes(search.toLowerCase())
    ),
    [employees, search]
  );

  const sortedPayments = useMemo(() => 
    [...salaryPayments].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [salaryPayments]
  );

  const totalSalaries = employees.reduce((sum, e) => sum + (e.salary || 0), 0);
  const totalPaidThisMonth = salaryPayments
    .filter(p => {
      const paymentDate = new Date(p.date || p.period_date);
      const now = new Date();
      return paymentDate.getMonth() === now.getMonth() && 
             paymentDate.getFullYear() === now.getFullYear() &&
             (p.payment_type === 'salary' || !p.payment_type);
    })
    .reduce((sum, p) => sum + p.amount, 0);

  const getSalaryTypeLabel = (type) => {
    const found = salaryTypes.find(t => t.en === type);
    return language === 'ar' ? (found?.ar || type) : (found?.en || type);
  };

  const getPaymentTypeLabel = (type) => {
    const found = paymentTypes.find(t => t.en === type);
    return language === 'ar' ? (found?.ar || type) : (found?.en || type);
  };

  const getPaymentTypeBadgeColor = (type) => {
    switch (type) {
      case 'salary': return 'bg-green-100 text-green-800';
      case 'bonus': return 'bg-blue-100 text-blue-800';
      case 'advance': return 'bg-yellow-100 text-yellow-800';
      case 'deduction': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="employees-title">{t('employees')}</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'إدارة الموظفين والرواتب' : 'Manage employees and salaries'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImportExcel}
            className="hidden"
            id="import-excel"
          />
          <Button variant="outline" onClick={() => document.getElementById('import-excel').click()}>
            <Upload size={18} className="me-2" />
            {language === 'ar' ? 'استيراد Excel' : 'Import Excel'}
          </Button>
          <Button variant="outline" onClick={handleExportExcel}>
            <Download size={18} className="me-2" />
            {language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
          </Button>
          {isAdmin && (
            <Button onClick={() => setDialogOpen(true)} data-testid="add-employee-btn">
              <Plus size={18} className="me-2" />
              {t('addEmployee')}
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'إجمالي الرواتب' : 'Total Salaries'}
                </p>
                <p className="text-2xl font-bold text-purple-600" data-testid="total-salaries">
                  {formatCurrency(totalSalaries)}
                </p>
              </div>
              <UserCog className="h-8 w-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'المدفوع هذا الشهر' : 'Paid This Month'}
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(totalPaidThisMonth)}
                </p>
              </div>
              <Banknote className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'عدد الموظفين' : 'Employees Count'}
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {employees.length}
                </p>
              </div>
              <UserCog className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sync Info Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700 flex items-center gap-3">
        <CheckCircle className="h-5 w-5 flex-shrink-0" />
        <div>
          <strong>{language === 'ar' ? 'متزامن:' : 'Synced:'}</strong>{' '}
          {language === 'ar' 
            ? 'بيانات الموظفين متزامنة تلقائياً بين قاعدة البيانات ولوحة التحكم وملف Excel.'
            : 'Employee data is automatically synced between database, dashboard, and Excel file.'}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="employees" data-testid="tab-employees">
            <UserCog className="h-4 w-4 me-2" />
            {language === 'ar' ? 'الموظفين' : 'Employees'}
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            <History className="h-4 w-4 me-2" />
            {language === 'ar' ? 'سجل الرواتب' : 'Payment History'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="mt-4">
          {/* Search */}
          <Card className="border-border/40 mb-4">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  placeholder={t('search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="ps-10"
                  data-testid="employees-search"
                />
              </div>
            </CardContent>
          </Card>

          {/* Employees Table */}
          <Card className="border-border/40">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <UserCog size={48} className="mx-auto mb-2 opacity-50" />
                  <p>{t('noData')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('employeeName')}</TableHead>
                        <TableHead>{t('position')}</TableHead>
                        <TableHead>{t('salary')}</TableHead>
                        <TableHead>{language === 'ar' ? 'نوع الراتب' : 'Salary Type'}</TableHead>
                        <TableHead>{t('phone')}</TableHead>
                        <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEmployees.map((employee) => (
                        <TableRow key={employee.id} data-testid={`employee-row-${employee.id}`}>
                          <TableCell className="font-medium">{employee.name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                              {language === 'ar'
                                ? positions.find((p) => p.en === employee.position)?.ar || employee.position
                                : employee.position}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-bold text-primary">
                            {formatCurrency(employee.salary)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="flex items-center gap-1 w-fit">
                              <Clock className="h-3 w-3" />
                              {getSalaryTypeLabel(employee.salary_type || 'monthly')}
                            </Badge>
                          </TableCell>
                          <TableCell dir="ltr">{employee.phone || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => openSalaryDialog(employee)}
                                data-testid={`pay-salary-${employee.id}`}
                              >
                                <Banknote size={14} className="me-1" />
                                {t('paySalary')}
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleEdit(employee)}
                                    data-testid={`edit-employee-${employee.id}`}
                                  >
                                    <Edit2 size={16} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDelete(employee.id)}
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`delete-employee-${employee.id}`}
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card className="border-border/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                {language === 'ar' ? 'سجل دفعات الرواتب' : 'Salary Payment History'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {salaryPayments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Banknote size={48} className="mx-auto mb-2 opacity-50" />
                  <p>{language === 'ar' ? 'لا توجد دفعات مسجلة' : 'No payments recorded'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'ar' ? 'التاريخ' : 'Date'}</TableHead>
                        <TableHead>{t('employeeName')}</TableHead>
                        <TableHead>{language === 'ar' ? 'النوع' : 'Type'}</TableHead>
                        <TableHead>{language === 'ar' ? 'الفترة' : 'Period'}</TableHead>
                        <TableHead>{t('amount')}</TableHead>
                        <TableHead>{language === 'ar' ? 'ملاحظات' : 'Notes'}</TableHead>
                        <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedPayments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell className="text-sm">
                            {formatDate(payment.date || payment.period_date)}
                          </TableCell>
                          <TableCell className="font-medium">{payment.employee_name}</TableCell>
                          <TableCell>
                            <Badge className={getPaymentTypeBadgeColor(payment.payment_type || 'salary')}>
                              {getPaymentTypeLabel(payment.payment_type || 'salary')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {payment.period_date ? formatDate(payment.period_date) : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="font-bold text-green-600">
                            {formatCurrency(payment.amount)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                            {payment.notes || '-'}
                          </TableCell>
                          <TableCell className="text-end">
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeletePayment(payment.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingEmployee ? t('edit') : t('addEmployee')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('employeeName')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="employee-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('position')}</Label>
                <Select
                  value={formData.position}
                  onValueChange={(value) => setFormData({ ...formData, position: value })}
                >
                  <SelectTrigger data-testid="employee-position-select">
                    <SelectValue placeholder={language === 'ar' ? 'اختر المنصب' : 'Select position'} />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos.en} value={pos.en}>
                        {language === 'ar' ? pos.ar : pos.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'نوع الراتب' : 'Salary Type'}</Label>
                <Select
                  value={formData.salary_type}
                  onValueChange={(value) => setFormData({ ...formData, salary_type: value })}
                >
                  <SelectTrigger data-testid="employee-salary-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {salaryTypes.map((type) => (
                      <SelectItem key={type.en} value={type.en}>
                        {language === 'ar' ? type.ar : type.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('salary')} (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.salary}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  required
                  data-testid="employee-salary-input"
                />
                <p className="text-xs text-muted-foreground">
                  {formData.salary_type === 'daily' && (language === 'ar' ? 'الراتب اليومي' : 'Daily rate')}
                  {formData.salary_type === 'weekly' && (language === 'ar' ? 'الراتب الأسبوعي' : 'Weekly rate')}
                  {formData.salary_type === 'monthly' && (language === 'ar' ? 'الراتب الشهري' : 'Monthly rate')}
                </p>
              </div>
              <div className="space-y-2">
                <Label>{t('hireDate')}</Label>
                <Input
                  type="date"
                  value={formData.hire_date}
                  onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                  data-testid="employee-hire-date-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('phone')}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  dir="ltr"
                  data-testid="employee-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('email')}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="employee-email-input"
                />
              </div>
            </div>

            {/* Info about sync */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
              <CheckCircle className="h-4 w-4 inline me-1" />
              {language === 'ar' 
                ? 'سيتم حفظ البيانات في قاعدة البيانات ولوحة التحكم وملف Excel تلقائياً'
                : 'Data will be saved to database, dashboard, and Excel file automatically'}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="employee-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pay Salary Dialog */}
      <Dialog open={salaryDialogOpen} onOpenChange={setSalaryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{t('paySalary')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePaySalary} className="space-y-4">
            {selectedEmployee && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">{t('employeeName')}</p>
                <p className="font-medium">{selectedEmployee.name}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {language === 'ar' ? 'الراتب' : 'Salary'}
                    </p>
                    <p className="text-xl font-bold text-primary">
                      {formatCurrency(selectedEmployee.salary)}
                    </p>
                  </div>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {getSalaryTypeLabel(selectedEmployee.salary_type || 'monthly')}
                  </Badge>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'نوع الدفعة' : 'Payment Type'}</Label>
                <Select
                  value={salaryData.payment_type}
                  onValueChange={(value) => setSalaryData({ ...salaryData, payment_type: value })}
                >
                  <SelectTrigger data-testid="payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentTypes.map((type) => (
                      <SelectItem key={type.en} value={type.en}>
                        {language === 'ar' ? type.ar : type.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'نوع الفترة' : 'Period Type'}</Label>
                <Select
                  value={salaryData.period_type}
                  onValueChange={(value) => setSalaryData({ ...salaryData, period_type: value })}
                >
                  <SelectTrigger data-testid="period-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {salaryTypes.map((type) => (
                      <SelectItem key={type.en} value={type.en}>
                        {language === 'ar' ? type.ar : type.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'تاريخ الفترة' : 'Period Date'}</Label>
                <Input
                  type="date"
                  value={salaryData.period_date}
                  onChange={(e) => setSalaryData({ ...salaryData, period_date: e.target.value })}
                  data-testid="period-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('amount')} (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={salaryData.amount}
                  onChange={(e) => setSalaryData({ ...salaryData, amount: e.target.value })}
                  required
                  data-testid="salary-amount-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSalaryDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="salary-submit-btn">
                <Banknote className="h-4 w-4 me-2" />
                {language === 'ar' ? 'تأكيد الدفع' : 'Confirm Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
